import { create } from "zustand";
import type {
  ExchangeId,
  Health,
  OrderBookSnapshot,
  Price,
  PriceChangeDirection,
} from "../types";
import { EXCHANGE_META, type FeeSummaryMeta, type LightningStatus, type WithdrawalLimitMeta } from "../config/exchangeMeta";
import { clearCachedPrices, persistPrice, readCachedPrices } from "./persistence";
import { EXCHANGES } from "../config/exchanges";

interface ExchangeRuntimeMeta {
  withdrawalFeeBtc: number;
  supportsLightning: boolean;
  lightningStatus: LightningStatus;
  lightningNote?: string;
  takerFee: number;
  usdtToman?: number;
  withdrawalLimits: WithdrawalLimitMeta;
  feeSummary: FeeSummaryMeta;
  updatedAt?: number;
}

interface PriceState {
  prices: Partial<Record<ExchangeId, Price>>;
  health: Record<ExchangeId, Health>;
  change: Record<ExchangeId, PriceChangeDirection>;
  orderBooks: Record<ExchangeId, OrderBookSnapshot>;
  marketMeta: Record<ExchangeId, ExchangeRuntimeMeta>;
  stale: Record<ExchangeId, boolean>;
  staleBadge: Record<ExchangeId, boolean>;
  initFromCache: () => Promise<void>;
  setPrice: (price: Price) => void;
  setHealth: (exchange: ExchangeId, status: Health) => void;
  refreshStale: (now?: number) => void;
  resetAll: () => Promise<void>;
}

export const STALE_PRICE_MAX_AGE_MS = 60_000;
export const STALE_PRICE_BADGE_AGE_MS = 5 * 60_000;

const DEFAULT_HEALTH: Record<ExchangeId, Health> = {
  nobitex: "CONNECTING",
  wallex: "CONNECTING",
  bitpin: "CONNECTING",
};

const DEFAULT_CHANGE: Record<ExchangeId, PriceChangeDirection> = {
  nobitex: "flat",
  wallex: "flat",
  bitpin: "flat",
};

function buildDefaultChange(): Record<ExchangeId, PriceChangeDirection> {
  return { ...DEFAULT_CHANGE };
}

function cloneOrderBook(snapshot: OrderBookSnapshot): OrderBookSnapshot {
  const bids = snapshot.bids
    .map((level) => ({ ...level }))
    .sort((a, b) => b.price - a.price);
  const asks = snapshot.asks
    .map((level) => ({ ...level }))
    .sort((a, b) => a.price - b.price);
  return {
    bids,
    asks,
    lastUpdated: snapshot.lastUpdated,
  };
}

function createEmptyOrderBook(): OrderBookSnapshot {
  return {
    bids: [],
    asks: [],
    lastUpdated: 0,
  };
}

function buildDefaultOrderBooks(): Record<ExchangeId, OrderBookSnapshot> {
  return {
    nobitex: createEmptyOrderBook(),
    wallex: createEmptyOrderBook(),
    bitpin: createEmptyOrderBook(),
  };
}

function buildDefaultPrices(): Partial<Record<ExchangeId, Price>> {
  return {};
}

function buildDefaultMeta(): Record<ExchangeId, ExchangeRuntimeMeta> {
  return {
    nobitex: {
      withdrawalFeeBtc: EXCHANGE_META.nobitex.withdrawalFeeBtc,
      supportsLightning: EXCHANGE_META.nobitex.supportsLightning,
      lightningStatus: EXCHANGE_META.nobitex.lightningStatus,
      lightningNote: EXCHANGE_META.nobitex.lightningNote,
      takerFee: EXCHANGE_META.nobitex.takerFee,
      usdtToman: EXCHANGE_META.nobitex.usdtToman,
      withdrawalLimits: EXCHANGE_META.nobitex.withdrawalLimits,
      feeSummary: EXCHANGE_META.nobitex.feeSummary,
    },
    wallex: {
      withdrawalFeeBtc: EXCHANGE_META.wallex.withdrawalFeeBtc,
      supportsLightning: EXCHANGE_META.wallex.supportsLightning,
      lightningStatus: EXCHANGE_META.wallex.lightningStatus,
      lightningNote: EXCHANGE_META.wallex.lightningNote,
      takerFee: EXCHANGE_META.wallex.takerFee,
      usdtToman: EXCHANGE_META.wallex.usdtToman,
      withdrawalLimits: EXCHANGE_META.wallex.withdrawalLimits,
      feeSummary: EXCHANGE_META.wallex.feeSummary,
    },
    bitpin: {
      withdrawalFeeBtc: EXCHANGE_META.bitpin.withdrawalFeeBtc,
      supportsLightning: EXCHANGE_META.bitpin.supportsLightning,
      lightningStatus: EXCHANGE_META.bitpin.lightningStatus,
      lightningNote: EXCHANGE_META.bitpin.lightningNote,
      takerFee: EXCHANGE_META.bitpin.takerFee,
      usdtToman: EXCHANGE_META.bitpin.usdtToman,
      withdrawalLimits: EXCHANGE_META.bitpin.withdrawalLimits,
      feeSummary: EXCHANGE_META.bitpin.feeSummary,
    },
  };
}

function buildDefaultStale(): Record<ExchangeId, boolean> {
  return {
    nobitex: true,
    wallex: true,
    bitpin: true,
  };
}

function buildDefaultStaleBadge(): Record<ExchangeId, boolean> {
  return {
    nobitex: false,
    wallex: false,
    bitpin: false,
  };
}

function mergeMeta(current: ExchangeRuntimeMeta, price: Price): ExchangeRuntimeMeta {
  return {
    withdrawalFeeBtc: price.withdrawalFeeBtc ?? current.withdrawalFeeBtc,
    supportsLightning: price.supportsLightning ?? current.supportsLightning,
    lightningStatus: current.lightningStatus,
    lightningNote: current.lightningNote,
    takerFee: price.takerFee ?? current.takerFee,
    usdtToman: price.usdtToman ?? current.usdtToman,
    withdrawalLimits: current.withdrawalLimits,
    feeSummary: current.feeSummary,
    updatedAt: price.ts ?? current.updatedAt ?? Date.now(),
  };
}

let cacheLoaded = false;

export const usePriceStore = create<PriceState>((set) => ({
  prices: buildDefaultPrices(),
  health: { ...DEFAULT_HEALTH },
  change: buildDefaultChange(),
  orderBooks: buildDefaultOrderBooks(),
  marketMeta: buildDefaultMeta(),
  stale: buildDefaultStale(),
  staleBadge: buildDefaultStaleBadge(),
  async initFromCache() {
    if (cacheLoaded) return;
    cacheLoaded = true;
    const cachedPrices = await readCachedPrices();
    set((state) => {
      const nextPrices = { ...state.prices };
      const nextChange = { ...state.change };
      const nextOrderBooks = { ...state.orderBooks };
      const nextMeta = { ...state.marketMeta };
      cachedPrices.forEach((price) => {
        nextPrices[price.ex] = price;
        nextChange[price.ex] = "flat";
        // orderBook رو ذخیره نمی‌کنیم چون حجم زیادی داره و به‌روزرسانی مداوم میشه
        nextMeta[price.ex] = mergeMeta(nextMeta[price.ex], price);
      });
      const nextStale = { ...state.stale };
      const nextStaleBadge = { ...state.staleBadge };
      (Object.keys(EXCHANGES) as ExchangeId[]).forEach((exchange) => {
        const timestamp = nextPrices[exchange]?.ts ?? 0;
        if (!timestamp) {
          nextStale[exchange] = true;
          nextStaleBadge[exchange] = false;
          return;
        }
        const age = Date.now() - timestamp;
        nextStale[exchange] = age > STALE_PRICE_MAX_AGE_MS;
        nextStaleBadge[exchange] = age > STALE_PRICE_BADGE_AGE_MS;
      });

      return {
        prices: nextPrices,
        change: nextChange,
        orderBooks: nextOrderBooks,
        marketMeta: nextMeta,
        stale: nextStale,
        staleBadge: nextStaleBadge,
      };
    });
  },
  setPrice(price) {
    set((state) => {
      const previousEntry = state.prices[price.ex];
      const hasPreviousTimestamp = (previousEntry?.ts ?? 0) > 0;
      const previousLast = previousEntry?.last;
      const hasValidPrevious = hasPreviousTimestamp && typeof previousLast === "number" && Number.isFinite(previousLast);
      const baseline = hasValidPrevious ? previousLast : price.last;
      const change: PriceChangeDirection = hasValidPrevious
        ? price.last > baseline
          ? "up"
          : price.last < baseline
            ? "down"
            : "flat"
        : "flat";
      const nextPrices = { ...state.prices, [price.ex]: price };
      const nextChange = { ...state.change, [price.ex]: change };

      const nextOrderBooks = { ...state.orderBooks };
      const hasIncomingOrderBook = Boolean(
        price.orderBook?.bids?.length && price.orderBook?.asks?.length,
      );
      if (hasIncomingOrderBook) {
        nextOrderBooks[price.ex] = cloneOrderBook(price.orderBook!);
      } else if (!nextOrderBooks[price.ex]?.bids.length && !nextOrderBooks[price.ex]?.asks.length) {
        nextOrderBooks[price.ex] = createEmptyOrderBook();
      }

      const nextMeta = {
        ...state.marketMeta,
        [price.ex]: mergeMeta(state.marketMeta[price.ex], price),
      };

      const nextStale = { ...state.stale, [price.ex]: false };
      const nextStaleBadge = { ...state.staleBadge, [price.ex]: false };

      return {
        prices: nextPrices,
        change: nextChange,
        orderBooks: nextOrderBooks,
        marketMeta: nextMeta,
        stale: nextStale,
        staleBadge: nextStaleBadge,
      };
    });
    void persistPrice(price);
  },
  setHealth(exchange, status) {
    set((state) => ({ health: { ...state.health, [exchange]: status } }));
  },
  refreshStale(now = Date.now()) {
    set((state) => {
      let didChange = false;
      const nextStale = { ...state.stale };
      const nextStaleBadge = { ...state.staleBadge };

      (Object.keys(EXCHANGES) as ExchangeId[]).forEach((exchange) => {
        const timestamp = state.prices[exchange]?.ts ?? state.marketMeta[exchange]?.updatedAt ?? 0;
        const hasTimestamp = Boolean(timestamp);
        const age = hasTimestamp ? now - timestamp : Number.POSITIVE_INFINITY;
        const shouldBeStale = !hasTimestamp || age > STALE_PRICE_MAX_AGE_MS;
        const shouldShowBadge = hasTimestamp && age > STALE_PRICE_BADGE_AGE_MS;

        if (nextStale[exchange] !== shouldBeStale) {
          nextStale[exchange] = shouldBeStale;
          didChange = true;
        }

        if (nextStaleBadge[exchange] !== shouldShowBadge) {
          nextStaleBadge[exchange] = shouldShowBadge;
          didChange = true;
        }
      });

      if (!didChange) {
        return state;
      }

      return {
        ...state,
        stale: nextStale,
        staleBadge: nextStaleBadge,
      };
    });
  },
  async resetAll() {
    cacheLoaded = false;
    await clearCachedPrices();
    set({
      prices: buildDefaultPrices(),
      health: { ...DEFAULT_HEALTH },
      change: buildDefaultChange(),
      orderBooks: buildDefaultOrderBooks(),
      marketMeta: buildDefaultMeta(),
      stale: buildDefaultStale(),
      staleBadge: buildDefaultStaleBadge(),
    });
  },
}));
